import { useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Truck, FileText, Clock, MapPin, Receipt, Container, Wrench, Layers, Package,
  Users, UserCheck, Building2, Landmark, LogOut, Radio, MapPinned, Menu, HardHat, PanelLeftClose, PanelLeftOpen, Palette,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "./common";
import { SharedCoreSync } from "./sharedcore/SharedCoreSync";

export const Layout = () => {
  const { user, tenant, logout, hasModule } = useAuth();
  const navigate = useNavigate();
  const isPlatformAdmin = user?.role === "platform_admin";
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("wfl_sidebar_collapsed") === "1");

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem("wfl_sidebar_collapsed", next ? "1" : "0");
      return next;
    });

  const NavItem = ({ to, icon: Icon, label, testId }) => (
    <NavLink
      to={to}
      data-testid={testId}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 border ${collapsed ? "lg:justify-center lg:px-2" : ""} ${
          isActive
            ? "bg-amber-600 text-white border-amber-600 shadow-sm"
            : "text-white/60 hover:text-white hover:bg-white/5 border-transparent"
        }`
      }
    >
      <Icon size={16} />
      <span className={collapsed ? "lg:hidden" : ""}>{label}</span>
    </NavLink>
  );

  const NavSection = ({ label, children }) => (
    <div className="mb-5">
      {collapsed && <div className="hidden lg:block mx-2 mb-2 border-t border-white/10" />}
      <p className={`px-3 mb-2 text-[10px] font-mono uppercase tracking-[0.25em] text-white/40 ${collapsed ? "lg:hidden" : ""}`}>{label}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );

  if (user?.role === "driver") return <Navigate to="/mobiledriver" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const togglePalette = () => {
    const el = document.documentElement;
    const next = el.getAttribute("data-palette") === "classic" ? "" : "classic";
    if (next) el.setAttribute("data-palette", next); else el.removeAttribute("data-palette");
    try { localStorage.setItem("wfl-palette", next || "new"); } catch (e) { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-background tactical-grid">
      <aside className={`fixed inset-y-0 left-0 w-64 ${collapsed ? "lg:w-16" : "lg:w-64"} bg-[#142B3B] border-r border-[#1E3C4B] flex flex-col z-40 transition-all duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="px-5 py-5 border-b border-white/10 lg:px-3">
          <div className={`flex items-center gap-2.5 ${collapsed ? "lg:flex-col lg:gap-3" : ""}`}>
            <img src="/logo.png?v=5" alt="Well Fleet Logic" className="w-9 h-9 object-contain flex-shrink-0 rounded-full ring-1 ring-white/25 bg-white/5" data-testid="brand-logo" />
            <span className={`leading-none whitespace-nowrap ${collapsed ? "lg:hidden" : ""}`} data-testid="brand-wordmark">
              <span className="text-xl font-bold tracking-tight text-white">Well Fleet <span className="text-[#D8C5A3]">Logic</span></span>
            </span>
            <button
              data-testid="sidebar-collapse-toggle"
              onClick={toggleCollapsed}
              className={`hidden lg:block p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-colors duration-150 ${collapsed ? "" : "ml-auto"}`}
              title={collapsed ? "Expand menu" : "Collapse menu"}
            >
              {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4" onClick={() => setSidebarOpen(false)}>
          {isPlatformAdmin ? (
            <NavSection label="Platform">
              <NavItem to="/admin/tenants" icon={Building2} label="Tenants & Billing" testId="nav-admin-tenants" />
            </NavSection>
          ) : (
            <>
              <NavSection label="Overview">
                <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />
              </NavSection>
              {hasModule("swd") && (
                <NavSection label="Hauling Logic">
                  <NavItem to="/swd/dispatch" icon={Radio} label="Dispatch Board" testId="nav-swd-dispatch" />
                  <NavItem to="/swd/tickets" icon={FileText} label="Job Tickets" testId="nav-swd-tickets" />
                  <NavItem to="/swd/time" icon={Clock} label="Timekeeping" testId="nav-swd-time" />
                  <NavItem to="/swd/map" icon={MapPin} label="GPS Check-ins" testId="nav-swd-map" />
                  <NavItem to="/swd/orders" icon={Receipt} label="Hauling Sales Orders" testId="nav-swd-orders" />
                </NavSection>
              )}
              {hasModule("fractank") && (
                <NavSection label="Rental Logic">
                  <NavItem to="/fractank/dispatch" icon={Container} label="Rental Dispatch" testId="nav-frac-dispatch" />
                  <NavItem to="/fractank/inventory" icon={Layers} label="Rentals Quick View" testId="nav-frac-inventory" />
                  <NavItem to="/fractank/orders" icon={FileText} label="Rental Jobs" testId="nav-frac-orders" />
                  <NavItem to="/fractank/map" icon={MapPin} label="Map View" testId="nav-frac-map" />
                </NavSection>
              )}
              {hasModule("fleet") && (
                <NavSection label="Fleet Logic">
                  <NavItem to="/fleet/work-orders" icon={Wrench} label="Work Orders" testId="nav-fleet-work-orders" />
                  {["admin", "shop_manager", "dispatcher"].includes(user?.role) && <NavItem to="/fleet/checklists" icon={FileText} label="Work Checklists" testId="nav-fleet-checklists" />}
                  <NavItem to="/fleet/inventory" icon={Package} label="Parts Inventory" testId="nav-fleet-inventory" />
                  {["admin", "shop_manager", "dispatcher"].includes(user?.role) && <NavItem to="/fleet/purchasing" icon={Receipt} label="Purchasing & Receiving" testId="nav-fleet-purchasing" />}
                </NavSection>
              )}
              <NavSection label="Shared Core">
                <NavItem to="/customers" icon={Users} label="Customers" testId="nav-customers" />
                <NavItem to="/requesters" icon={UserCheck} label="Requesters" testId="nav-requesters" />
                <NavItem to="/assets" icon={Truck} label="Asset Register" testId="nav-assets" />
                <NavItem to="/locations" icon={MapPinned} label="Locations" testId="nav-locations" />
                <NavItem to="/drivers" icon={HardHat} label="Drivers" testId="nav-drivers" />
                <NavItem to="/product-service-items" icon={Package} label="Product & Service Items" testId="nav-product-service" />
              </NavSection>
              {hasModule("accounting") && (
                <NavSection label="Accounting Logic">
                  <NavItem to="/accounting" icon={Landmark} label="Invoicing" testId="nav-accounting" />
                </NavSection>
              )}
              {["admin", "shop_manager"].includes(user?.role) && (
                <NavSection label="Team">
                  <NavItem to="/users" icon={Users} label="Users & Roles" testId="nav-users" />
                </NavSection>
              )}
            </>
          )}
        </nav>

        <div className="px-4 py-4 border-t border-white/10">
          <div className={`flex items-center justify-between gap-2 ${collapsed ? "lg:justify-center" : ""}`}>
            <div className={`min-w-0 ${collapsed ? "lg:hidden" : ""}`}>
              <p className="text-sm font-medium text-white truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-white/40">{user?.role?.replace("_", " ")}</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="palette-toggle"
                onClick={togglePalette}
                className="p-2 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-colors duration-200"
                title="Switch color theme (new / classic)"
              >
                <Palette size={16} />
              </button>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="p-2 rounded-md text-white/50 hover:text-red-300 hover:bg-red-500/15 transition-colors duration-200"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      <div className={`${collapsed ? "lg:ml-16" : "lg:ml-64"} flex flex-col min-h-screen transition-all duration-200`}>
        {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-backdrop" />}
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-slate-900/80 border-b border-slate-800 px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden p-1 -ml-1 text-slate-400 hover:text-slate-100" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu">
              <Menu size={20} />
            </button>
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse-dot flex-shrink-0" data-testid="connectivity-indicator" />
            <span className="text-sm font-medium text-slate-200 truncate" data-testid="header-tenant-name">
              {isPlatformAdmin ? "Platform Administration" : tenant?.name}
            </span>
            {tenant && <span className="hidden sm:inline-flex"><StatusBadge status={tenant.plan} testId="header-plan-badge" /></span>}
          </div>
          <div className="flex items-center gap-3">
            {tenant && (
              <span className="hidden md:inline text-xs font-mono text-slate-500" data-testid="header-module-count">
                {tenant.enabled_modules?.length || 0} MODULES ACTIVE
              </span>
            )}
            <StatusBadge status={user?.role} testId="header-role-badge" />
          </div>
        </header>
        <main className="flex-1 min-w-0 p-6 lg:p-8">
          <SharedCoreSync />
          <Outlet />
        </main>
      </div>
    </div>
  );
};
